import {PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  CreateModificationFiltersCommand,
  DeleteModificationFilterCommand,
  IModificationFiltersClient,
  InstructionValidationStatuses,
  ModificationFilterDto,
  ModificationFiltersClient,
  UpdateModificationFilterCommand,
} from "app/ApiClient";
import { IState } from "slices";
import { LoadingState, checkAndThrowInstructionValidationException } from "types";
import { initialModificationFiltersState } from "./modificationFiltersState";

const modificationFiltersClient: IModificationFiltersClient = new ModificationFiltersClient(
  process.env.REACT_APP_API_URL,
);

export const createModificationFilters = createAsyncThunk(
  "modificationFilters/createModificationFilter",
  async (command: CreateModificationFiltersCommand, { rejectWithValue, dispatch }) => {
    try {
      const result = await modificationFiltersClient.create(command);
      checkAndThrowInstructionValidationException(result);
      if (result.validationStatus === InstructionValidationStatuses.Success) {
        dispatch(getModificationFilters(command.modificationId));         
      }
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getModificationFilters = createAsyncThunk(
  " modificationFilters/getVariablesFilters",
  async (modificationId: number, { rejectWithValue }) => {
    try {
      const result = await modificationFiltersClient.getVariableFilters(modificationId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateModificationFilter = createAsyncThunk(
  " modificationFilters/updateFilterDefinitions",
  async (command: UpdateModificationFilterCommand, { rejectWithValue, dispatch  }) => {
    try {
      const result = await modificationFiltersClient.update(command);
      checkAndThrowInstructionValidationException(result);
      if (result.validationStatus === InstructionValidationStatuses.Success) {
          dispatch(getModificationFilters(command.modificationId)); 
      }
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteModificationFilter = createAsyncThunk(
  " modificationFilters/removeModificationFilter",
  async (command: DeleteModificationFilterCommand, { rejectWithValue }) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const result = await modificationFiltersClient.delete(command);
      checkAndThrowInstructionValidationException(result);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const modificationFilterSlice = createSlice({
  name: "modificationFilter",
  initialState: initialModificationFiltersState,
  reducers: {
    resetModificationFilters: (state) => {
      state.modificationFilters = [];
      state.updateLoading = LoadingState.idle;
    },
    setFinishDateFilter: (state, { payload }: PayloadAction<Nullable<ModificationFilterDto>>) => {
      state.finishDateFilter = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createModificationFilters.pending, (state) => {
      state.updateLoading = LoadingState.pending;
    });
    builder.addCase(createModificationFilters.fulfilled, (state, { payload }) => {
      state.updateLoading = LoadingState.succeed;
      state.modificationFilters = [...state.modificationFilters, payload];
    });
    builder.addCase(createModificationFilters.rejected, (state, { error: { message } }) => {
      state.updateLoading = LoadingState.failed;
      state.errorMessage = message;
    });

    builder.addCase(getModificationFilters.pending, (state) => {
      state.getLoading = LoadingState.pending;
      state.modificationFilters = [];
      state.finishDateFilter = null;
    });
    builder.addCase(getModificationFilters.fulfilled, (state, { payload }) => {
      state.getLoading = LoadingState.succeed;
      state.modificationFilters = payload;
    });
    builder.addCase(getModificationFilters.rejected, (state, { error: { message } }) => {
      state.getLoading = LoadingState.failed;
      state.modificationFilters = [];
      state.errorMessage = message;
    });

    builder.addCase(updateModificationFilter.pending, (state) => {
      state.updateLoading = LoadingState.pending;
    });
    builder.addCase(updateModificationFilter.fulfilled, (state, { payload }) => {
      state.updateLoading = LoadingState.succeed;
      const update = state.modificationFilters.filter((f) => f.id !== payload.id);
      state.modificationFilters = [...update, payload];
    });
    builder.addCase(updateModificationFilter.rejected, (state, { error: { message } }) => {
      state.updateLoading = LoadingState.failed;
      state.errorMessage = message;
    });

    builder.addCase(deleteModificationFilter.pending, (state) => {
      state.updateLoading = LoadingState.pending;
    });
    builder.addCase(deleteModificationFilter.fulfilled, (state, { payload }) => {
      state.updateLoading = LoadingState.succeed;
      state.modificationFilters = state.modificationFilters.filter((f) => f.id !== payload.id);
    });
    builder.addCase(deleteModificationFilter.rejected, (state, { error: { message } }) => {
      state.updateLoading = LoadingState.failed;
      state.errorMessage = message;
    });
  },
});

export const { resetModificationFilters, setFinishDateFilter } = modificationFilterSlice.actions;
export const modificationFiltersSelector = (state: IState) => state.modificationFilters;
export default modificationFilterSlice.reducer
